import React, { useMemo, useState } from 'react'
import { useAccessContext } from '../../../context/AccessContext'
import { AdvancedOptions } from '../../../context/AdminExperienceContext'
import { useI18n } from '../../../i18n/I18nContext'
import { createWorkPosting } from '../../../services/access'
import type { SkillCatalogResult } from '../../../services/skills'
import type { AccessibleLocation, Organization, WorkPostingPayload, WorkPostingResult } from '../../../types/access'
import { SkillPicker } from '../../ui/SkillPicker'
import { ErrorBanner, FormModal } from './AdminUiPrimitives'

type Props = {
  isOpen: boolean
  onClose: () => void
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  selectedOrgId: string
  onCreated: (result: WorkPostingResult) => void
}

const addDay = (date: string) => {
  const value = new Date(`${date}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() + 1)
  return value.toISOString().slice(0, 10)
}

const hasValidTimezone = (timezone?: string | null) => {
  if (!timezone) return false
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone }).format()
    return true
  } catch {
    return false
  }
}

const errorMessage = (cause: any) => {
  const code = cause?.response?.data?.reasonCode
  if (code === 'JOB_DESTINATION_NOT_READY') return 'This worksite is not ready to publish. Add a complete address, resolved map position, and valid timezone, then try again.'
  if (code === 'WORK_POSTING_SKILLS_INVALID') return 'The selected skills could not be validated. Review Required Skills before posting.'
  if (code === 'WORK_POSTING_SHIFT_DELEGATION_DENIED') return 'Workforce Partner Access does not allow shifts at this worksite. Ask a relationship administrator to review the site grant.'
  if (code === 'WORK_POSTING_TIME_INVALID') return 'The selected local time is not valid at this worksite. Review the date, time, and worksite timezone.'
  return cause?.response?.data?.message || 'Unable to post this work. Review the details and try again.'
}

export const PostWorkWizard: React.FC<Props> = ({
  isOpen,
  onClose,
  organizations,
  accessibleLocations,
  selectedOrgId,
  onCreated,
}) => {
  const { t } = useI18n()
  const { accessContext } = useAccessContext()
  const [step, setStep] = useState(0)
  const [title, setTitle] = useState('')
  const [skills, setSkills] = useState<SkillCatalogResult[]>([])
  const [locationId, setLocationId] = useState('')
  const [date, setDate] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [workersNeeded, setWorkersNeeded] = useState(1)
  const [notes, setNotes] = useState('')
  const [payRate, setPayRate] = useState('')
  const [billRate, setBillRate] = useState('')
  const [visibilityType, setVisibilityType] = useState<'private' | 'own_workers' | 'shared_agencies' | 'marketplace_public'>('private')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const selectedLocation = accessibleLocations.find((location) => location.id === locationId)
  const selectedOrganization = organizations.find((organization) => organization.id === selectedOrgId)
    || organizations.find((organization) => organization.id === (selectedLocation?.ownerOrgId || selectedLocation?.owner_org_id))
    || organizations[0]
  const locationOwnerOrgId = selectedLocation?.ownerOrgId || selectedLocation?.owner_org_id || null
  const locationOwnerOrg = organizations.find((organization) => organization.id === locationOwnerOrgId)
  const companyOrgId = selectedOrganization?.org_type === 'company'
    ? selectedOrganization.id
    : (selectedLocation?.companyOrgId
      || selectedLocation?.company_org_id
      || (locationOwnerOrg?.org_type === 'company' || selectedLocation?.ownerOrgType === 'company' || selectedLocation?.owner_org_type === 'company'
        ? locationOwnerOrgId
        : null))
  const managingAgencyOrgId = selectedOrganization?.org_type === 'agency' ? selectedOrganization.id : null
  const locationTenantId = selectedLocation?.locationTenantId || selectedLocation?.tenant_id || null
  const endDate = date && start && end && end <= start ? addDay(date) : date
  const steps = [t('Work'), t('Where and when'), t('Workforce need'), t('Review and post')]
  const canContinue = [
    Boolean(title.trim()),
    Boolean(selectedLocation && date && start && end && hasValidTimezone(selectedLocation.timezone)),
    workersNeeded > 0,
    true,
  ][step]
  const locationLabel = selectedLocation?.name || selectedLocation?.label || selectedLocation?.location_key || t('Selected worksite')

  const close = () => {
    if (saving) return
    onClose()
  }

  const submit = async (action: WorkPostingPayload['action']) => {
    if (!selectedOrganization || !selectedLocation || !date || !start || !end || !selectedLocation.timezone) return
    setSaving(true)
    setError('')
    try {
      const result = await createWorkPosting({
        action,
        job: {
          createdByOrgId: selectedOrganization.id,
          companyOrgId,
          managingAgencyOrgId,
          locationId: selectedLocation.id,
          locationTenantId,
          title: title.trim(),
          description: notes.trim() || null,
          workMode: 'onsite',
          visibilityType,
          payRate: payRate ? Number(payRate) : null,
          billRate: billRate ? Number(billRate) : null,
          currencyCode: 'CAD',
          requiredSkills: skills.map((skill) => ({ skillId: skill.id, requirementType: 'required' as const })),
          metadata: { createdThrough: 'post_work_wizard' },
        },
        shift: {
          locationId: selectedLocation.id,
          locationTenantId,
          startsAtLocal: `${date}T${start}`,
          endsAtLocal: `${endDate}T${end}`,
          sourceTimezone: selectedLocation.timezone,
          workersNeeded,
          workMode: 'onsite',
          payRate: payRate ? Number(payRate) : null,
          billRate: billRate ? Number(billRate) : null,
          currencyCode: 'CAD',
          metadata: { notes: notes.trim() || undefined },
        },
      })
      onCreated(result)
      onClose()
    } catch (cause: any) {
      setError(t(errorMessage(cause)))
    } finally {
      setSaving(false)
    }
  }

  const reviewSummary = useMemo(() => [
    title.trim(),
    locationLabel,
    date,
    start && end ? `${start} – ${end}${endDate !== date ? ` ${t('(next day)')}` : ''}` : '',
    `${workersNeeded} ${workersNeeded === 1 ? t('worker') : t('workers')}`,
    skills.length ? `${t('Required skills')}: ${skills.map((skill) => skill.canonicalName).join(', ')}` : '',
  ].filter(Boolean), [date, end, endDate, locationLabel, skills, start, t, title, workersNeeded])

  return (
    <FormModal
      title={t('Post Work')}
      description={t('Tell us what is needed. Nexus will create the canonical Job and Shift together.')}
      isOpen={isOpen}
      onClose={close}
      closeOnBackdrop={!saving}
      size="lg"
      footer={(
        <div className="admin-modal-actions">
          {step > 0 ? <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => setStep(step - 1)}>{t('Back')}</button> : null}
          {step < steps.length - 1 ? <button type="button" className="btn btn-primary" disabled={!canContinue || saving} onClick={() => setStep(step + 1)}>{t('Continue')}</button> : (
            <>
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => submit('save_draft')}>{t('Save Draft')}</button>
              <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => submit('post')}>{t('Post Work')}</button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={() => submit('post_and_find')}>{saving ? t('Posting…') : t('Post & Find Workers')}</button>
            </>
          )}
        </div>
      )}
    >
      <ol className="guided-step-list" aria-label={t('Post Work progress')}>
        {steps.map((label, index) => (
          <li key={label} aria-current={index === step ? 'step' : undefined} className={index < step ? 'complete' : index === step ? 'active' : ''}>
            <span aria-hidden="true">{index < step ? '✓' : index + 1}</span> {label}
          </li>
        ))}
      </ol>
      <ErrorBanner message={error} testId="post-work-error" />
      {step === 0 ? (
        <div className="stack" data-testid="post-work-step-work">
          <label className="field-label">{t('What work needs to be done?')}<input className="input" value={title} onChange={(event) => setTitle(event.target.value)} autoFocus required placeholder={t('e.g. Security Guard')} /></label>
          <SkillPicker selectedSkills={skills} onChange={setSkills} label={t('Required skills')} placeholder={t('Search canonical skills')} requestContext="job_required_skills" />
        </div>
      ) : null}
      {step === 1 ? (
        <div className="stack" data-testid="post-work-step-place-time">
          <label className="field-label">{t('Worksite')}<select className="input" value={locationId} onChange={(event) => setLocationId(event.target.value)} required><option value="">{t('Choose a worksite')}</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{location.name || location.label || location.location_key}</option>)}</select></label>
          {selectedLocation && !hasValidTimezone(selectedLocation.timezone) ? <div className="admin-state admin-state--warning" role="alert"><strong>{t('This worksite cannot be published yet.')}</strong><p>{t('Add a valid IANA timezone in Locations / Geofences, then return here.')}</p></div> : null}
          <label className="field-label">{t('Date')}<input type="date" className="input" value={date} onChange={(event) => setDate(event.target.value)} required /></label>
          <div className="admin-form-grid">
            <label className="field-label">{t('Start')}<input type="time" className="input" value={start} onChange={(event) => setStart(event.target.value)} required /></label>
            <label className="field-label">{t('End')}<input type="time" className="input" value={end} onChange={(event) => setEnd(event.target.value)} required /></label>
          </div>
          {selectedLocation?.timezone ? <p className="helper">{t('Times use the worksite timezone')}: {selectedLocation.timezone}</p> : null}
        </div>
      ) : null}
      {step === 2 ? (
        <div className="stack" data-testid="post-work-step-need">
          <fieldset><legend>{t('How many workers?')}</legend><div className="quantity-control"><button type="button" className="btn btn-secondary" aria-label={t('Remove one worker')} onClick={() => setWorkersNeeded(Math.max(1, workersNeeded - 1))}>−</button><output aria-live="polite">{workersNeeded}</output><button type="button" className="btn btn-secondary" aria-label={t('Add one worker')} onClick={() => setWorkersNeeded(Math.min(10000, workersNeeded + 1))}>+</button></div></fieldset>
          <label className="field-label">{t('Notes or instructions (optional)')}<textarea className="input" value={notes} onChange={(event) => setNotes(event.target.value)} rows={4} /></label>
          <AdvancedOptions>
            {accessContext?.rateVisibility?.canViewPayRate ? <label className="field-label">{t('Pay rate')}<input type="number" min="0" step="0.01" className="input" value={payRate} onChange={(event) => setPayRate(event.target.value)} /></label> : null}
            {accessContext?.rateVisibility?.canViewBillRate ? <label className="field-label">{t('Bill rate')}<input type="number" min="0" step="0.01" className="input" value={billRate} onChange={(event) => setBillRate(event.target.value)} /></label> : null}
            <label className="field-label">{t('Visibility')}<select className="input" value={visibilityType} onChange={(event) => setVisibilityType(event.target.value as typeof visibilityType)}><option value="private">{t('Private')}</option><option value="own_workers">{t('Own workers')}</option><option value="shared_agencies">{t('Workforce partners')}</option><option value="marketplace_public">{t('Public marketplace')}</option></select></label>
          </AdvancedOptions>
        </div>
      ) : null}
      {step === 3 ? (
        <div className="stack" data-testid="post-work-step-review">
          <h4>{t('Review your work request')}</h4>
          <ul>{reviewSummary.map((line) => <li key={line}>{line}</li>)}</ul>
          <p className="helper">{t('Posting creates one canonical Job with one canonical Shift. Backend permissions, approval, relationship, and worksite rules still apply.')}</p>
        </div>
      ) : null}
    </FormModal>
  )
}
